import type {
  CollaborativeMapClusterResponse,
  CollaborativeMapPinResponse,
  CollaborativeMapSubmissionsResponse,
} from '@/service/engageFrontendGateway';
import type { CollabMapPin, SubmissionDetail } from '../CollaborativeMapContext';
import { toPins } from './collaborativeMapResponseMapper';

// ──────────────────────────────────────────────────────────────────────────────
// Gateway-shape mocks — fake the GET /collaborative-maps/.../submissions
// response when NEXT_PUBLIC_COLLAB_MAP_MOCK=true. Consumed by
// `getCollabMapSubmissions` in engageFrontendGateway.ts.
// ──────────────────────────────────────────────────────────────────────────────

// Pins scattered around the Vancouver map center used in collaborative_map_qb_mock.json.
// inputIds match the pindrop_map inputs in that fixture so pin colors resolve correctly.
export const MOCK_COLLAB_MAP_PINS: CollaborativeMapPinResponse[] = [
  {
    lat: 49.29,
    lng: -123.12,
    submissionId: 'mock-sub-001',
    residentId: null,
    questionId: 'q-1',
    inputId: '1f9c2a32-8f43-4279-bffa-aed3365a8801',
    completedAt: '2026-04-20T10:00:00Z',
  },
  {
    lat: 49.278,
    lng: -123.135,
    submissionId: 'mock-sub-002',
    residentId: null,
    questionId: 'q-1',
    inputId: '1f9c2a32-8f43-4279-bffa-aed3365a8802',
    completedAt: '2026-04-20T11:30:00Z',
  },
  {
    lat: 49.285,
    lng: -123.105,
    submissionId: 'mock-sub-003',
    residentId: null,
    questionId: 'q-1',
    inputId: '1f9c2a32-8f43-4279-bffa-aed3365a8803',
    completedAt: '2026-04-21T09:15:00Z',
  },
  {
    lat: 49.275,
    lng: -123.11,
    submissionId: 'mock-sub-004',
    residentId: null,
    questionId: 'q-1',
    inputId: '1f9c2a32-8f43-4279-bffa-aed3365a8801',
    completedAt: '2026-04-21T14:00:00Z',
  },
  {
    lat: 49.292,
    lng: -123.14,
    submissionId: 'mock-sub-005',
    residentId: null,
    questionId: 'q-1',
    inputId: '1f9c2a32-8f43-4279-bffa-aed3365a8802',
    completedAt: '2026-04-22T08:45:00Z',
  },
];

// Counts span the bubble-size buckets (small <100, medium <1000, large >=1000)
// so all three visual states render in dev.
export const MOCK_COLLAB_MAP_CLUSTERS: CollaborativeMapClusterResponse[] = [
  { lat: 49.29, lng: -123.12, count: 2400 },
  { lat: 49.27, lng: -123.14, count: 1100 },
  { lat: 49.3, lng: -123.1, count: 540 },
  { lat: 49.26, lng: -123.09, count: 180 },
  { lat: 49.31, lng: -123.16, count: 42 },
];

// The backend decides individual vs cluster based on pin count in the viewport,
// not zoom level. The mock always returns individual pins for local development.
// To test cluster rendering, set mode to 'cluster' and use MOCK_COLLAB_MAP_CLUSTERS.
export const buildCollabMapMock = (): CollaborativeMapSubmissionsResponse => ({
  total: MOCK_COLLAB_MAP_PINS.length,
  mode: 'individual',
  pins: MOCK_COLLAB_MAP_PINS,
});

// ──────────────────────────────────────────────────────────────────────────────
// Internal-shape mocks — already-mapped data, faked at the service layer (e.g.
// `fetchSubmissionDetail`). Derived from the gateway-shape mocks so the two
// can never drift on submissionId / completedAt.
// ──────────────────────────────────────────────────────────────────────────────

export const MOCK_COLLABORATIVE_PINS: CollabMapPin[] = toPins({
  total: MOCK_COLLAB_MAP_PINS.length,
  mode: 'individual',
  pins: MOCK_COLLAB_MAP_PINS,
});

export const MOCK_SUBMISSION_DETAIL: SubmissionDetail = {
  residentId: null,
  likes: 7,
  completedAt: '2026-04-20T10:00:00Z',
  data: [
    {
      sectionId: 'a1b2c3d4-0000-0000-0000-000000000003',
      order: 1,
      title: 'Your thoughts',
      questions: [
        {
          questionId: 'q-text-input',
          order: 1,
          title: 'Tell us more about what you pinned',
          responses: [
            {
              responseId: 'mock-resp-001',
              inputId: 'q-text-input',
              locale: 'en',
              inputAnswer:
                'The sidewalks here are badly cracked and uneven. I nearly tripped last week and I have seen older residents struggle to walk through this stretch with mobility aids.',
              translatedInputAnswer: null,
              comment: '',
              translatedComment: null,
              createdAt: '2026-04-20T10:00:00Z',
            },
          ],
        },
        {
          questionId: 'q-frequency',
          order: 2,
          title: 'How often do you visit this area?',
          responses: [
            {
              responseId: 'mock-resp-002',
              inputId: 'opt-daily',
              locale: 'en',
              inputAnswer: 'Daily',
              translatedInputAnswer: null,
              comment: '',
              translatedComment: null,
              createdAt: '2026-04-20T10:01:00Z',
            },
          ],
        },
        {
          questionId: 'q-improvements',
          order: 3,
          title: 'What type of improvements would you like to see?',
          responses: [
            {
              responseId: 'mock-resp-003a',
              inputId: 'opt-sidewalk',
              locale: 'en',
              inputAnswer: 'Sidewalk repair',
              translatedInputAnswer: null,
              comment: '',
              translatedComment: null,
              createdAt: '2026-04-20T10:01:30Z',
            },
            {
              responseId: 'mock-resp-003b',
              inputId: 'opt-lighting',
              locale: 'en',
              inputAnswer: 'Better street lighting',
              translatedInputAnswer: null,
              comment: '',
              translatedComment: null,
              createdAt: '2026-04-20T10:01:30Z',
            },
            {
              responseId: 'mock-resp-003c',
              inputId: 'opt-accessibility',
              locale: 'en',
              inputAnswer: 'Accessible curb cuts',
              translatedInputAnswer: null,
              comment: '',
              translatedComment: null,
              createdAt: '2026-04-20T10:01:30Z',
            },
          ],
        },
        {
          questionId: 'q-urgency',
          order: 4,
          title: 'How urgent is this issue?',
          responses: [
            {
              responseId: 'mock-resp-004',
              inputId: 'opt-urgent',
              locale: 'en',
              inputAnswer: 'Very urgent',
              translatedInputAnswer: null,
              comment: '',
              translatedComment: null,
              createdAt: '2026-04-20T10:02:00Z',
            },
          ],
        },
        {
          questionId: 'q-additional',
          order: 5,
          title: 'Any additional comments for the city?',
          responses: [
            {
              responseId: 'mock-resp-005',
              inputId: 'q-additional',
              locale: 'en',
              inputAnswer:
                'This block is heavily used by students walking to the community centre. Please prioritize it in the next maintenance cycle.',
              translatedInputAnswer: null,
              comment: '',
              translatedComment: null,
              createdAt: '2026-04-20T10:02:30Z',
            },
          ],
        },
      ],
    },
  ],
};
